import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { Role } from '../../types';
import { createSSOProvider, getRoles } from '../../utils/auth/authService';
import SSOProviderEditMode from '../../components/auth/SSOProviderEditMode';

type ProviderType = 'oidc' | 'saml';

const AddSSOProviderPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [providerType, setProviderType] = useState<ProviderType>('oidc');

  // Form state
  const [editName, setEditName] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editDefaultRole, setEditDefaultRole] = useState<string | undefined>(undefined);
  const [editAutoCreateUsers, setEditAutoCreateUsers] = useState(true);
  
  // OIDC config
  const [editOidcClientId, setEditOidcClientId] = useState('');
  const [editOidcClientSecret, setEditOidcClientSecret] = useState('');
  const [editOidcDiscoveryUrl, setEditOidcDiscoveryUrl] = useState('');
  const [editOidcScopes, setEditOidcScopes] = useState('openid, email, profile');
  
  // SAML config
  const [editSamlEntityId, setEditSamlEntityId] = useState('');
  const [editSamlSsoUrl, setEditSamlSsoUrl] = useState('');
  const [editSamlSloUrl, setEditSamlSloUrl] = useState('');
  const [editSamlX509Cert, setEditSamlX509Cert] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const rolesData = await getRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to load roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      setError('Provider name is required');
      return;
    }

    if (providerType === 'oidc') {
      if (!editOidcClientId.trim() || !editOidcClientSecret.trim() || !editOidcDiscoveryUrl.trim()) {
        setError('OIDC Client ID, Client Secret, and Discovery URL are required');
        return;
      }
    }

    if (providerType === 'saml') {
      if (!editSamlEntityId.trim() || !editSamlSsoUrl.trim() || !editSamlX509Cert.trim()) {
        setError('SAML Entity ID, SSO URL, and X509 Certificate are required');
        return;
      }
    }

    setSaving(true);
    setError('');

    try {
      const newProvider: any = {
        name: editName,
        provider_type: providerType,
        enabled: editEnabled,
        default_role: editDefaultRole || undefined,
        auto_create_users: editAutoCreateUsers,
      };
      
      if (providerType === 'oidc') {
        newProvider.oidc_config = {
          client_id: editOidcClientId,
          client_secret: editOidcClientSecret,
          discovery_url: editOidcDiscoveryUrl,
          scopes: editOidcScopes.split(',').map(s => s.trim()).filter(Boolean),
          attribute_mapping: {
            email: 'email',
            first_name: 'given_name',
            last_name: 'family_name',
          },
        };
      }
      
      if (providerType === 'saml') {
        newProvider.saml_config = {
          entity_id: editSamlEntityId,
          sso_url: editSamlSsoUrl,
          slo_url: editSamlSloUrl || undefined,
          x509_cert: editSamlX509Cert,
          attribute_mapping: {
            email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
            first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
            last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
          },
        };
      }
      
      await createSSOProvider(newProvider);
      navigate('/auth/sso-providers');
    } catch (err) {
      setError('Failed to create SSO provider');
      console.error('Failed to create:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  // Create a mock provider object for the EditMode component
  const mockProvider: any = {
    provider_id: 'new',
    name: editName,
    provider_type: providerType,
    enabled: editEnabled,
    default_role: editDefaultRole,
    auto_create_users: editAutoCreateUsers,
  };

  if (providerType === 'oidc') {
    mockProvider.oidc_config = {
      client_id: editOidcClientId,
      client_secret: editOidcClientSecret,
      discovery_url: editOidcDiscoveryUrl,
      scopes: editOidcScopes.split(',').map(s => s.trim()).filter(Boolean),
      attribute_mapping: {
        email: 'email',
        first_name: 'given_name',
        last_name: 'family_name',
      },
    };
  }

  if (providerType === 'saml') {
    mockProvider.saml_config = {
      entity_id: editSamlEntityId,
      sso_url: editSamlSsoUrl,
      slo_url: editSamlSloUrl,
      x509_cert: editSamlX509Cert,
      attribute_mapping: {
        email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
        last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
      },
    };
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/auth/sso-providers')}
            style={{
              padding: 8,
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>
            Add SSO Provider
          </h1>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 500,
            opacity: saving ? 0.5 : 1,
          }}
        >
          <Save size={18} />
          {saving ? 'Creating...' : 'Create Provider'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      {/* Provider Type Selection */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
        marginBottom: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0 }}>
          Provider Type
        </h2>
        <div style={{ display: 'flex', gap: 16 }}>
          <button
            onClick={() => setProviderType('oidc')}
            style={{
              flex: 1,
              padding: 16,
              background: providerType === 'oidc' ? theme.buttonBackground : 'transparent',
              color: providerType === 'oidc' ? theme.buttonText : theme.text,
              border: `2px solid ${providerType === 'oidc' ? theme.accent : theme.border}`,
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
          >
            OIDC / OAuth 2.0
          </button>
          <button
            onClick={() => setProviderType('saml')}
            style={{
              flex: 1,
              padding: 16,
              background: providerType === 'saml' ? theme.buttonBackground : 'transparent',
              color: providerType === 'saml' ? theme.buttonText : theme.text,
              border: `2px solid ${providerType === 'saml' ? theme.accent : theme.border}`,
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
          >
            SAML 2.0
          </button>
        </div>
      </div>

      {/* Form - Reuse EditMode component */}
      <SSOProviderEditMode
        provider={mockProvider}
        roles={roles}
        theme={theme}
        editName={editName}
        editEnabled={editEnabled}
        editDefaultRole={editDefaultRole}
        editAutoCreateUsers={editAutoCreateUsers}
        editOidcClientId={editOidcClientId}
        editOidcClientSecret={editOidcClientSecret}
        editOidcDiscoveryUrl={editOidcDiscoveryUrl}
        editOidcScopes={editOidcScopes}
        editSamlEntityId={editSamlEntityId}
        editSamlSsoUrl={editSamlSsoUrl}
        editSamlSloUrl={editSamlSloUrl}
        editSamlX509Cert={editSamlX509Cert}
        onNameChange={setEditName}
        onEnabledChange={setEditEnabled}
        onDefaultRoleChange={setEditDefaultRole}
        onAutoCreateUsersChange={setEditAutoCreateUsers}
        onOidcClientIdChange={setEditOidcClientId}
        onOidcClientSecretChange={setEditOidcClientSecret}
        onOidcDiscoveryUrlChange={setEditOidcDiscoveryUrl}
        onOidcScopesChange={setEditOidcScopes}
        onSamlEntityIdChange={setEditSamlEntityId}
        onSamlSsoUrlChange={setEditSamlSsoUrl}
        onSamlSloUrlChange={setEditSamlSloUrl}
        onSamlX509CertChange={setEditSamlX509Cert}
      />
    </div>
  );
};

export default AddSSOProviderPage;
